<script lang="ts" setup>
import { type IconName } from './UiIcon.vue';

interface Props {
  loading?: boolean;
  disabled?: boolean;
  variant?:
    | 'default'
    | 'flat'
    | 'primary'
    | 'primary-text'
    | 'error'
    | 'error-text'
    | 'dark';
  size?: 'default' | 'small' | 'medium' | 'large';
  block?: boolean;
  textCasing?: 'capitalize' | 'normal' | 'uppercase' | 'lowercase';
  type?: 'button' | 'submit' | 'reset';
  borderRadius?: 'default' | 'rounded';
  icon?: IconName;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disabled: false,
  variant: 'default',
  size: 'default',
  block: false,
  textCasing: 'uppercase',
  borderRadius: 'default',
  type: 'button',
});

const emit = defineEmits<{
  (e: 'click', p: MouseEvent): void;
}>();

const handleClick = (e: MouseEvent) => {
  emit('click', e);
};

const disabled = computed(() => {
  if (props.disabled) {
    return true;
  }

  if (props.loading) {
    return true;
  }

  return false;
});

const iconSizeClass = computed(() => {
  if (props.size === 'small') {
    return 'icon-size-s';
  } else if (props.size === 'large') {
    return 'icon-size-m';
  }

  return 'icon-size-sm';
});

const mainClasses = computed(() => {
  const classes: (
    | 'dark'
    | 'flat'
    | 'primary'
    | 'primary-text'
    | 'error'
    | 'error-text'
    | 'loading'
    | 'disabled'
    | 'small'
    | 'medium'
    | 'large'
    | 'block'
    | 'capitalize'
    | 'normal'
    | 'uppercase'
    | 'lowercase'
    | 'rounded'
  )[] = [];

  if (props.variant === 'primary') {
    classes.push('primary');
  } else if (props.variant === 'error') {
    classes.push('error');
  } else if (props.variant === 'flat') {
    classes.push('flat');
  } else if (props.variant === 'dark') {
    classes.push('dark');
  } else if (props.variant === 'primary-text') {
    classes.push('primary-text');
  } else if (props.variant === 'error-text') {
    classes.push('error-text');
  }

  if (props.textCasing === 'uppercase') {
    classes.push('uppercase');
  } else if (props.textCasing === 'capitalize') {
    classes.push('capitalize');
  } else if (props.textCasing === 'lowercase') {
    classes.push('lowercase');
  }

  if (props.size === 'small') {
    classes.push('small');
  } else if (props.size === 'large') {
    classes.push('large');
  } else if (props.size === 'medium') {
    classes.push('medium');
  }

  if (props.loading) {
    classes.push('loading');
  }

  if (disabled.value) {
    classes.push('disabled');
  }

  if (props.block) {
    classes.push('block');
  }

  if (props.borderRadius === 'rounded') {
    classes.push('rounded');
  }

  return classes;
});
</script>

<template>
  <button
    :class="mainClasses"
    :disabled="disabled"
    :type="props.type"
    class="ui-button"
    @click="handleClick"
  >
    <svg v-if="loading" class="svg-class" fill="none" viewBox="0 0 24 24">
      <circle
        class="circle-class"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      />
      <path
        class="path-class"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        fill="currentColor"
      />
    </svg>

    <UiIcon v-if="!loading && icon" :name="icon" :class="iconSizeClass" />
    <slot />
  </button>
</template>

<style lang="scss" scoped>
.ui-button {
  color: var(--color-primary-700);
  border-color: var(--color-primary-700);
  background-color: var(--color-gray-50);
  &:hover {
    background-color: var(--color-gray-100);
  }
  display: inline-flex;
  padding: var(--space-8) var(--space-16);
  font-size: var(--font-size-14);
  line-height: var(--line-height-20);
  font-weight: var(--font-weight-medium);
  justify-content: center;
  align-items: center;
  border: 1px solid var(--color-gray-300);
  gap: var(--space-8);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition-property: background-color, border-color, color, fill, stroke;
  cursor: pointer;
  border-radius: var(--border-radius-6);

  &:disabled {
    cursor: not-allowed;
    background-color: rgb(203 213 225);
  }

  /*&.uppercase {
    text-transform: uppercase;
  }*/
}

.ui-button.primary {
  color: white;
  background-color: var(--color-primary-600);
  &:hover {
    background-color: var(--color-primary-700);
  }
  &:disabled {
    background-color: var(--color-primary-300);
  }
}

.ui-button.primary-text {
  font-size: var(--font-size-12);
  line-height: var(--line-height-16);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-600);
  border-color: transparent;
  box-shadow: none;
  background: transparent;

  &:disabled,
  &:hover {
    color: var(--color-primary-800);
  }
}

.ui-button.error {
  color: white;
  background-color: rgb(239 68 68);
  &:hover {
    background-color: rgb(220 38 38);
  }
  &:disabled {
    background-color: rgb(153 27 27);
  }
}

.ui-button.error-text {
  background-color: transparent;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-12);
  line-height: var(--line-height-16);
  border-color: transparent;
  color: rgb(220 38 38);
}

.ui-button.flat {
  border: none;
  box-shadow: none;
  background: transparent;
  padding: var(--space-6);

  &:hover {
    background-color: var(--color-gray-100);
  }

  &:disabled {
    color: var(--color-gray-400);
    cursor: not-allowed;
  }
}

.ui-button.dark {
  color: white;
  background-color: rgb(71 85 105);
  &:hover {
    background-color: rgb(30 41 59);
  }
  &:disabled {
    background-color: rgb(30 41 59);
  }
}

.ui-button.disabled {
  cursor: not-allowed;
}

.ui-button.small {
  padding-top: var(--space-4);
  padding-bottom: var(--space-4);
  padding-left: var(--space-12);
  padding-right: var(--space-12);
  font-size: var(--font-size-14);
  line-height: var(--line-height-20);
  font-weight: var(--font-weight-regular);
}

.ui-button.medium {
  height: 40px;
  text-align: center;
}

.ui-button.large {
  min-width: 120px;
  height: 55px;
  text-align: center;
}

.ui-button.rounded {
  border-radius: var(--border-radius-6);
}
.block {
  width: 100%;
  justify-content: center;
}
.svg-class {
  margin-left: var(--space-4);
  margin-right: var(--space-12);
  height: 1.25rem;
  width: 1.25rem;
  color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
.circle-class {
  opacity: 0.25;
}
.path-class {
  opacity: 0.75;
}
</style>
